import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useInView } from "framer-motion";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMembership } from "../../contexts/MembershipContext";
import { hapticSelection } from "../../lib/haptics";
import { useSuperInvestorHoldings } from "./hooks/useSuperInvestorHoldings";
import { formatNumber } from "../Dashboard/Charts/utils/formatNumber";
import { profileCardSx } from "./components/InvestorWorkspace";
import { getPersonPortraitImageProps } from "./personPortrait";

export interface Investor {
  name: string;
  cik: string;
  imageUrl: string;
  fund: string;
  assetsUnderManagement?: string;
}
interface InvestorCardProps {
  investor: Investor;
  onClick?: (investor: Investor) => void;
}
const InvestorCard = ({ investor, onClick }: InvestorCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMembershipActive } = useMembership();
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useInView(cardRef, { once: true, margin: "160px" });
  const locked = !isMembershipActive && investor.cik !== "0001067983";
  const { data, isLoading } = useSuperInvestorHoldings(
    investor.cik,
    inView && !locked,
  );
  const largest = [...(data?.data ?? [])]
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);
  const value = data?.totalPortfolioValue;
  const loading = !locked && !data && (!inView || isLoading);
  // /start-free-trial renders the RevenueCat paywall on native, matching the
  // Radar's locked sector cards.
  const handleClick = () => {
    if (!locked) {
      onClick?.(investor);
      return;
    }
    hapticSelection();
    navigate("/start-free-trial");
  };
  const label = locked
    ? `${investor.name}'s portfolio, available with Insight Pro`
    : `View ${investor.name}'s portfolio`;
  return (
    <Tooltip title={locked ? "Available with Insight Pro" : ""}>
      <Box ref={cardRef} sx={{ minWidth: 0 }}>
        <ButtonBase
          onClick={handleClick}
          aria-label={label}
          sx={profileCardSx(theme)}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Avatar
              {...getPersonPortraitImageProps(investor.imageUrl, 44)}
              alt=""
              sx={{ width: 44, height: 44, borderRadius: 1.5 }}
            >
              {investor.name[0]}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h2"
                noWrap
                title={investor.name}
                sx={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                }}
              >
                {investor.name}
              </Typography>
              <Typography
                noWrap
                title={investor.fund}
                sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}
              >
                {investor.fund}
              </Typography>
            </Box>
            {locked && (
              <Typography
                component="span"
                sx={{
                  flexShrink: 0,
                  alignSelf: "center",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 0.75,
                  px: 1,
                  lineHeight: "20px",
                }}
              >
                Pro
              </Typography>
            )}
            {!locked && (
              <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <Typography
                  aria-label="Reported portfolio value"
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.375rem" },
                    lineHeight: 1.2,
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {loading && <Skeleton width={80} />}
                  {!loading && value !== undefined && (
                    <>${formatNumber(value)}</>
                  )}
                  {!loading && value === undefined && "—"}
                </Typography>
                {data && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      mt: 0.25,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Q{data.quarter} {data.year}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {locked && (
            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.text.primary, 0.03),
              }}
            >
              <LockOutlinedIcon
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                Available with Insight Pro
              </Typography>
            </Box>
          )}
          {!locked && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)",
                alignItems: "start",
                gap: 1.5,
                mt: 2.5,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                >
                  Portfolio
                </Typography>
                {data && (
                  <Typography
                    sx={{ fontSize: "0.75rem", mt: 0.5, whiteSpace: "nowrap" }}
                  >
                    {data.data.length.toLocaleString()} positions
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                <Typography
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                >
                  Top three holdings
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.75,
                    minHeight: 24,
                  }}
                  aria-label="Largest positions"
                >
                  {largest.map((holding, index) => {
                    const label = holding.symbol?.trim()
                      ? holding.symbol.trim()
                      : holding.nameOfIssuer;
                    return (
                      <Chip
                        component="span"
                        key={`${holding.securityCusip}-${index}`}
                        label={label}
                        title={holding.nameOfIssuer}
                        size="small"
                        sx={{
                          maxWidth: "100%",
                          height: 24,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: "primary.main",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    );
                  })}
                  {loading && (
                    <Skeleton variant="rounded" width="100%" height={24} />
                  )}
                  {!loading && !data && (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Holdings currently unavailable
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};
export default InvestorCard;
